import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { Alert, AppBar, AppBarProps, Toolbar, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { isPurchaseBlockedLockType } from '@trm/shared/utils/lockTypePredicates';
import { ReactNode, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { handlerDrawerOpen, useGetMenuMaster } from '~/api/menu';
import IconButton from '~/components/@extended/IconButton';
import GlobalBanners from '~/components/globalBanners/globalBanners';
import { DRAWER_WIDTH, MINI_DRAWER_WIDTH } from '~/config';
import useAuth from '~/hooks/useAuth';
import { useBlockPurchaseFlag } from '~/hooks/useBlockPurchaseFlag';
import useConfig from '~/hooks/useConfig';
import { ThemeMode } from '~/types/config';

import AppBarStyled from './AppBarStyled';
import HeaderContent from './HeaderContent';
import HeaderCustomAlert from './HeaderCustomAlert';

interface HeaderProps {
  userLocked: boolean;
  userLockedType: string | undefined;
  onPaymentMethodsChange?: (hasPaymentMethods: boolean) => void;
  externalBannerOffset?: number;
}

const Header: React.FC<HeaderProps> = ({
  userLocked,
  userLockedType,
  onPaymentMethodsChange,
  externalBannerOffset = 0,
}) => {
  const { user } = useAuth();
  const theme = useTheme();
  const downLG = useMediaQuery(theme.breakpoints.down('lg'));
  const { onChangeMiniDrawer } = useConfig();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const { useBlockPurchase } = useBlockPurchaseFlag();

  const headerContent = useMemo(
    () => (
      <HeaderContent
        disableCTAs={isPurchaseBlockedLockType(userLockedType?.toLowerCase()) || useBlockPurchase}
      />
    ),
    [userLockedType, useBlockPurchase],
  );

  const iconBackColor = theme.palette.mode === ThemeMode.DARK ? 'background.default' : 'grey.100';

  const verificationLockMessage: ReactNode = !userLocked &&
    user?.needsVerification &&
    (user?.kycStatus !== null ? user?.kycStatus?.toLowerCase() !== 'max-kyc-attempts' : true) && (
      <Alert color="error" icon={false} sx={{ width: '100%', justifyContent: 'center' }}>
        Your account requires{' '}
        <NavLink
          to={`/dashboard/profile/details?tab=idv`}
          style={{ color: theme.palette.text.primary, textDecoration: 'underline' }}
        >
          Identity Verification (IDV)
        </NavLink>
        . Purchasing & new account creation is temporarily disabled.
      </Alert>
    );

  const globalBanners: ReactNode = (
    <GlobalBanners onPaymentMethodsChange={onPaymentMethodsChange} />
  );

  const getUserLockMessage = (): ReactNode => {
    if (!userLocked) return null;

    let message = '';
    if (user?.kycStatus?.toLowerCase() === 'max-kyc-attempts') {
      message =
        'Your account is locked due to too many identity verification attempts. Please contact Trader Support.';
    } else {
      switch (userLockedType?.toLowerCase()) {
        case 'purchase':
          message =
            'Your account is currently locked from making new purchases. Please contact Trader Support for assistance.';
          break;
        case 'reset':
          message =
            'Your account reset functionality is temporarily locked. Please contact Trader Support to resolve this issue.';
          break;
        case 'full':
          message =
            'Your account is fully locked. Please contact Trader Support immediately to address this issue.';
          break;
        case 'other':
          message =
            'Your account has been locked. Please contact Trader Support immediately to address this issue.';
          break;
        case 'xfa-activation':
        case 'xfa-activation-call-down':
        case 'xfa-activation-stxfa':
        case 'xfa-activation-pro':
          message =
            'You are currently unable to activate new XFAs. This may be due to an active LFA account or a restriction placed by an admin. Please check your account status for more details.';
          break;
        case 'rtp':
          return (
            <HeaderCustomAlert
              message="You are currently enrolled in our Responsible Trader Program. For more information click"
              link={{
                href: 'https://help.topstep.com/en/articles/13620045-what-is-the-responsible-trading-program',
                label: 'here',
              }}
            />
          );
        default:
          message = 'Your account has been locked. Please contact Trader Support.';
      }
    }

    return (
      <Alert color="error" icon={false} sx={{ width: '100%', justifyContent: 'center' }}>
        {message}
      </Alert>
    );
  };

  const userLockMessage = getUserLockMessage();

  const handleDrawerToggle = () => {
    handlerDrawerOpen(!drawerOpen);
    onChangeMiniDrawer(drawerOpen);
  };

  const mainHeader: ReactNode = (
    <Toolbar>
      <IconButton
        data-cy="open-drawer"
        aria-label="open drawer"
        onClick={handleDrawerToggle}
        edge="start"
        color="secondary"
        variant="light"
        sx={{
          color: 'text.primary',
          bgcolor: drawerOpen ? 'transparent' : iconBackColor,
          ml: { xs: 0, lg: -2 },
        }}
      >
        {!drawerOpen ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
      </IconButton>
      {headerContent}
    </Toolbar>
  );

  const appBar: AppBarProps = {
    position: 'fixed',
    color: 'inherit',
    elevation: 0,
    sx: {
      borderBottom: `1px solid ${theme.palette.divider}`,
      zIndex: 1200,
      width: {
        xs: '100%',
        lg: drawerOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : `calc(100% - ${MINI_DRAWER_WIDTH}px)`,
      },
      top: externalBannerOffset || undefined,
    },
  };

  return (
    <>
      {!downLG ? (
        <div>
          <AppBarStyled open={drawerOpen} {...appBar}>
            {mainHeader}
            {globalBanners}
            {/* TODO: move messages below to globalBanners */}
            {verificationLockMessage}
            {userLockMessage}
          </AppBarStyled>
        </div>
      ) : (
        <AppBar {...appBar}>{mainHeader}</AppBar>
      )}
    </>
  );
};

export default Header;
